import React, { useEffect } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

/**
 * NOTE: Despite the name, these now render a FADE-IN (opacity + translateY-up,
 * ease-out) to match the product pages' FadeIn dynamics — not a typing effect.
 * The original typewriter API is preserved (text/as/className/delay/enabled/
 * onComplete) so existing call sites keep working; `speed` and `cursor` are
 * accepted but ignored.
 */

interface TypewriterProps {
  text: string;
  speed?: number;       // ignored (kept for API compatibility)
  delay?: number;       // ms before the fade starts
  duration?: number;    // fade duration (ms)
  distance?: number;    // translateY distance (px)
  as?: keyof JSX.IntrinsicElements;
  className?: string;
  cursor?: boolean;     // ignored
  /** If false, the fade won't start until enabled becomes true */
  enabled?: boolean;
  onComplete?: () => void;
}

export const Typewriter: React.FC<TypewriterProps> = ({
  text,
  delay = 0,
  duration = 600,
  distance = 30,
  as: Tag = 'span',
  className = '',
  enabled = true,
  onComplete,
}) => {
  // revealAfterMs: 0 → reveal ONLY on real scroll-into-view (no premature
  // 1.2s safety-net reveal), so sections fade as you scroll down a long page.
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1, revealAfterMs: 0 });
  const show = isVisible && enabled;

  useEffect(() => {
    if (!show) return;
    const t = setTimeout(() => onComplete?.(), delay + duration);
    return () => clearTimeout(t);
  }, [show, delay, duration, onComplete]);

  return (
    <div
      ref={ref}
      style={{
        opacity: show ? 1 : 0,
        transform: show ? 'none' : `translateY(${distance}px)`,
        transition: `opacity ${duration}ms ease-out ${delay}ms, transform ${duration}ms ease-out ${delay}ms`,
      }}
    >
      {React.createElement(Tag, { className }, text)}
    </div>
  );
};

interface TypewriterSequenceProps {
  items: {
    text: string;
    as?: keyof JSX.IntrinsicElements;
    className?: string;
    speed?: number; // ignored
  }[];
  delay?: number;
  gap?: number;      // ignored (replaced by a fixed stagger)
  duration?: number;
  distance?: number;
  cursor?: boolean;  // ignored
  onComplete?: () => void;
}

// Stagger between successive hero lines (ms).
const SEQUENCE_STAGGER = 250;

export const TypewriterSequence: React.FC<TypewriterSequenceProps> = ({
  items,
  delay = 0,
  duration = 600,
  distance = 30,
  onComplete,
}) => {
  // revealAfterMs: 0 → reveal ONLY on real scroll-into-view (no premature
  // 1.2s safety-net reveal), so sections fade as you scroll down a long page.
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1, revealAfterMs: 0 });

  useEffect(() => {
    if (!isVisible) return;
    const total = delay + Math.max(0, items.length - 1) * SEQUENCE_STAGGER + duration;
    const t = setTimeout(() => onComplete?.(), total);
    return () => clearTimeout(t);
  }, [isVisible, delay, items.length, duration, onComplete]);

  return (
    <div ref={ref}>
      {items.map((item, i) => {
        const Tag = item.as || 'span';
        const d = delay + i * SEQUENCE_STAGGER;
        return React.createElement(
          Tag,
          {
            key: i,
            className: item.className,
            style: {
              display: 'block',
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'none' : `translateY(${distance}px)`,
              transition: `opacity ${duration}ms ease-out ${d}ms, transform ${duration}ms ease-out ${d}ms`,
            } as React.CSSProperties,
          },
          item.text,
        );
      })}
    </div>
  );
};
